import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { Message } from '@arco-design/web-react'
import { getApiBaseUrl } from '@/lib/apiBaseUrl'
import i18n from '@/i18n'

// 标准响应格式
export interface ApiResponse<T = unknown> {
  success: boolean
  message: string
  data: T
}

// 扩展请求配置，支持自定义选项
declare module 'axios' {
  interface AxiosRequestConfig {
    // 是否跳过全局错误处理
    skipErrorHandler?: boolean
    // 是否跳过重复请求检查
    skipDuplicateCheck?: boolean
    // 请求重试次数
    retryCount?: number
    // 是否保留后端完整响应体（用于 /api/pricing 这类 data 外还有元数据的接口）
    preserveResponseData?: boolean
    // 当前重试次数（内部使用）
    __retryCount?: number
  }
}

// 获取用户 ID 用于 `New-API-User` 请求头（协议兼容字面量，与后端 Go 代码耦合，不做品牌替换）
const getUserIdFromStorage = (): string => {
  try {
    const userStr = localStorage.getItem('auth-storage')
    if (userStr) {
      const parsed = JSON.parse(userStr)
      return parsed?.state?.user?.id?.toString() || ''
    }

    // 兼容 old web 的存储结构
    const legacyUserStr = localStorage.getItem('user')
    if (legacyUserStr) {
      const legacyUser = JSON.parse(legacyUserStr)
      return legacyUser?.id?.toString() || ''
    }
  } catch (e) {
    console.error('Failed to parse auth storage:', e)
  }
  return ''
}

// 创建 axios 实例
const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  timeout: 60000, // 60秒超时，某些请求（如渠道测试）可能需要较长时间
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-store',
  },
})

// 正在进行的 GET 请求 Map（用于防止重复请求）
const pendingGetRequests = new Map<string, Promise<unknown>>()

// 生成请求唯一键
const generateRequestKey = (url: string, config?: AxiosRequestConfig): string => {
  const params = config?.params ? JSON.stringify(config.params) : '{}'
  return `${url}?${params}`
}

// ==================== 请求拦截器 ====================
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 添加认证 token
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 添加 `New-API-User` 头（兼容上游协议，后端通过此头识别用户）
    config.headers['New-API-User'] = getUserIdFromStorage()
    config.headers['Accept-Language'] = i18n.language || 'zh-CN'

    // 防止重复 GET 请求
    if (config.method?.toLowerCase() === 'get' && !config.skipDuplicateCheck) {
      const requestKey = generateRequestKey(config.url || '', config)

      if (pendingGetRequests.has(requestKey)) {
        // 返回 pending 中的 Promise，实现请求复用
        return Promise.reject(new axios.Cancel('Duplicate request')) as unknown as typeof config
      }
    }

    // 初始化重试计数
    if (config.retryCount && config.retryCount > 0) {
      config.__retryCount = config.__retryCount || 0
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// ==================== 响应拦截器 ====================
// 响应拦截器返回 T 而不是 AxiosResponse<T>，这里通过 any 绕开类型检查
// eslint-disable-next-line @typescript-eslint/no-explicit-any
;(apiClient.interceptors.response.use as any)(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (response: any) => {
    // 清除 pending 中的 GET 请求
    if (response.config.method?.toLowerCase() === 'get') {
      const requestKey = generateRequestKey(response.config.url || '', response.config)
      pendingGetRequests.delete(requestKey)
    }

    // 返回标准格式的 data
    // 后端返回格式: { success: boolean, message: string, data: any }
    const responseData = response.data as ApiResponse

    // 如果后端返回 success: false，也当作错误处理
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
      if (!responseData.success) {
        const error = new Error(responseData.message || '请求失败')
        ;(error as AxiosError).response = { data: responseData } as typeof response
        throw error
      }
      if (response.config.preserveResponseData) {
        return responseData
      }
      // 标准格式带 data 壳时只返回 data；部分旧接口（如 /api/channel/test/:id、update_balance）直接顶格返回
      if (Object.prototype.hasOwnProperty.call(responseData, 'data')) {
        return (responseData as ApiResponse<unknown>).data
      }
      return responseData
    }

    return responseData
  },

  // 错误响应处理
  async (error: AxiosError<ApiResponse>) => {
    const config = error.config

    // 清除 pending 中的 GET 请求
    if (config && config.method?.toLowerCase() === 'get') {
      const requestKey = generateRequestKey(config.url || '', config)
      pendingGetRequests.delete(requestKey)
    }

    // 如果是取消的重复请求，直接返回 pending 中的 Promise
    if (axios.isCancel(error)) {
      const requestKey = generateRequestKey(config?.url || '', config)
      if (pendingGetRequests.has(requestKey)) {
        return pendingGetRequests.get(requestKey)
      }
      return Promise.reject(error)
    }

    // 请求重试逻辑
    if (config && config.retryCount && config.retryCount > 0) {
      config.__retryCount = config.__retryCount || 0

      if (config.__retryCount < config.retryCount) {
        config.__retryCount += 1
        console.log(`[API Retry] ${config.url} - Attempt ${config.__retryCount}`)

        // 延迟重试（指数退避）
        const delay = Math.pow(2, config.__retryCount) * 1000
        await new Promise((resolve) => setTimeout(resolve, delay))

        return apiClient(config)
      }
    }

    // 如果配置要求跳过全局错误处理，直接返回错误
    if (config?.skipErrorHandler) {
      return Promise.reject(error)
    }

    // 处理特定状态码
    const status = error.response?.status
    const responseData = error.response?.data
    const message = responseData?.message || error.message || '未知错误'

    switch (status) {
      case 401:
        // 未登录或登录过期；登录入口已统一到门户（`/`）侧 AuthPanel，不再强制定位 /login
        Message.error('登录已过期，请重新登录')
        useAuthStore.getState().logout()
        window.location.href = '/'
        break

      case 403:
        // 权限不足或需要安全验证
        if (message.includes('验证') || message.includes('verification')) {
          Message.error('需要进行安全验证')
          // TODO: 可以触发全局验证弹窗
        } else {
          Message.error('权限不足')
        }
        break

      case 429:
        // 请求过于频繁
        Message.error('请求过于频繁，请稍后再试')
        break

      case 500:
      case 502:
      case 503:
      case 504:
        // 服务器错误
        Message.error('服务器错误，请稍后再试')
        break

      default:
        // 其他错误显示后端返回的消息
        if (message) {
          Message.error(message)
        }
        break
    }

    return Promise.reject(error)
  }
)

// ==================== 便捷请求方法 ====================

/**
 * GET 请求
 * @param url 请求地址
 * @param params 查询参数
 * @param config 额外配置
 */
export const get = <T = unknown>(
  url: string,
  params?: Record<string, unknown> | object,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.get(url, { params, ...config }) as Promise<T>
}

/**
 * POST 请求
 * @param url 请求地址
 * @param data 请求体
 * @param config 额外配置
 */
export const post = <T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.post(url, data, config) as Promise<T>
}

/**
 * PUT 请求
 * @param url 请求地址
 * @param data 请求体
 * @param config 额外配置
 */
export const put = <T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.put(url, data, config) as Promise<T>
}

/**
 * DELETE 请求
 * @param url 请求地址
 * @param config 额外配置
 */
export const del = <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  return apiClient.delete(url, config) as Promise<T>
}

/**
 * PATCH 请求
 * @param url 请求地址
 * @param data 请求体
 * @param config 额外配置
 */
export const patch = <T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.patch(url, data, config) as Promise<T>
}

// 导出默认实例
export default apiClient
